import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, ShoppingCart, Share2, Heart, X } from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';

const ProductDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState('');

  // Fix double /media/ URLs
  const fixImageUrl = (url) => {
    if (!url) return url;
    // Handle PHP backend image paths
    if (url.startsWith('uploads/')) {
      return `/backend-php/${url}`;
    }
    // Handle Django double media issue
    if (url.includes('/media/media/')) {
      return url.replace('/media/media/', '/media/');
    }
    // Handle absolute URLs
    if (url.startsWith('http')) {
      return url;
    }
    // Default fallback
    return url;
  };

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        
        // Load single product from PHP API
        console.log('ProductDetailPage: Fetching product with ID:', id);
        const apiUrl = `/backend-php/api/products.php?id=${id}`;
        console.log('ProductDetailPage: API URL:', apiUrl);
        
        let response;
        try {
          response = await fetch(apiUrl, {
            cache: 'no-cache',
            headers: {
              'Cache-Control': 'no-cache, no-store, must-revalidate',
              'Pragma': 'no-cache',
              'Expires': '0'
            }
          });
        } catch (fetchError) {
          console.log('ProductDetailPage: Fetch error:', fetchError);
          throw new Error('Network error: ' + fetchError.message);
        }
        
        console.log('ProductDetailPage: Response status:', response.status);
        console.log('ProductDetailPage: Response ok:', response.ok);
        
        if (!response.ok) {
          const errorText = await response.text();
          console.log('ProductDetailPage: Error response:', errorText);
          throw new Error('Product not found');
        }
        
        const data = await response.json();
        
        console.log('ProductDetailPage: Full API response:', data);
        
        if (data && data.success && data.product) {
          console.log('ProductDetailPage: Found product:', data.product);
          setProduct(data.product);
        } else {
          console.log('ProductDetailPage: Product not found for ID:', id);
          console.log('ProductDetailPage: API response structure:', data);
          console.log('ProductDetailPage: data.success:', data?.success);
          console.log('ProductDetailPage: data.product:', data?.product);
          throw new Error(`Product with ID ${id} not found`);
        }
      } catch (error) {
        console.error("Error fetching product:", error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchProduct();
    }
  }, [id]);

  const handleImageClick = (imageUrl) => {
    setSelectedImage(imageUrl);
    setShowImageModal(true);
  };

  const closeImageModal = () => {
    setShowImageModal(false);
    setSelectedImage('');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Product Not Found</h1>
          <p className="text-gray-600 mb-6">The product you're looking for doesn't exist or has been removed.</p>
          <Link
            to="/products"
            className="inline-flex items-center px-4 py-2 border border-transparent text-base font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Products
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back
            </button>
            <div className="text-sm text-gray-500">
              <Link to="/" className="hover:text-primary-600">Home</Link>
              <span className="mx-2">/</span>
              <Link to="/products" className="hover:text-primary-600">Products</Link>
              <span className="mx-2">/</span>
              <span className="text-gray-900">{product.name}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Product Details */}
      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Product Image */}
          <div className="space-y-4">
            <div className="aspect-square bg-white rounded-lg shadow-lg overflow-hidden">
              <img
                src={fixImageUrl(product.image) || 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgdmlld0JveD0iMCAwIDQwMCAzMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSI0MDAiIGhlaWdodD0iMzAwIiBmaWxsPSIjZjhmOWZhIi8+CjxyZWN0IHg9IjE1MCIgeT0iMTAwIiB3aWR0aD0iMTAwIiBoZWlnaHQ9IjgwIiBmaWxsPSIjNmM3NTdkIi8+Cjx0ZXh0IHg9IjIwMCIgeT0iMjIwIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBmaWxsPSIjNmM3NTdkIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTYiPk5vIEltYWdlPC90ZXh0Pgo8L3N2Zz4K'}
                alt={product.name}
                className="w-full h-full object-cover cursor-pointer hover:scale-105 transition-transform duration-300"
                onClick={() => handleImageClick(fixImageUrl(product.image))}
                onError={(e) => {
                  console.log('ProductDetailPage: Image failed to load:', product.image);
                  e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgdmlld0JveD0iMCAwIDQwMCAzMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSI0MDAiIGhlaWdodD0iMzAwIiBmaWxsPSIjZjhmOWZhIi8+CjxyZWN0IHg9IjE1MCIgeT0iMTAwIiB3aWR0aD0iMTAwIiBoZWlnaHQ9IjgwIiBmaWxsPSIjNmM3NTdkIi8+Cjx0ZXh0IHg9IjIwMCIgeT0iMjIwIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBmaWxsPSIjNmM3NTdkIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTYiPk5vIEltYWdlPC90ZXh0Pgo8L3N2Zz4K';
                }}
              />
            </div>
            
            {/* Image Actions */}
            <div className="flex space-x-2">
              <button className="flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50">
                <Share2 className="w-4 h-4" />
              </button>
              <button className="flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50">
                <Heart className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Product Info */}
          <div className="space-y-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">{product.name}</h1>
              <p className="text-lg text-primary-600 font-semibold mb-2">{product.category}</p>
              <p className="text-sm text-gray-500 mb-4">Product Code: {product.code}</p>
            </div>

            <div className="flex items-center space-x-4">
              <span className="text-3xl font-bold text-gray-900">Ksh {product.price?.toLocaleString()}</span>
              <span className="text-sm text-gray-500">In Stock</span>
            </div>

            <div className="prose max-w-none">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Description</h3>
              <p className="text-gray-700 leading-relaxed">{product.description}</p>
            </div>

            {product.specifications && (
              <div className="prose max-w-none">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Specifications</h3>
                <div className="text-gray-700 whitespace-pre-line">{product.specifications}</div>
              </div>
            )}

            {product.applications && (
              <div className="prose max-w-none">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Applications</h3>
                <div className="text-gray-700 whitespace-pre-line">{product.applications}</div>
              </div>
            )}

            <div className="flex space-x-4">
              <Link
                to={`/contact?product=${encodeURIComponent(product.name)}&code=${encodeURIComponent(product.code)}`}
                className="flex-1 bg-primary-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-primary-700 transition-colors flex items-center justify-center"
              >
                <ShoppingCart className="w-5 h-5 mr-2" />
                Request Quote
              </Link>
              <Link
                to="/contact"
                className="flex-1 border border-primary-600 text-primary-600 px-6 py-3 rounded-lg font-semibold hover:bg-primary-50 transition-colors text-center"
              >
                Contact Sales
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Image Modal */}
      {showImageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="relative max-w-4xl max-h-full">
            <button
              onClick={closeImageModal}
              className="absolute -top-4 -right-4 bg-white rounded-full p-2 hover:bg-gray-100 transition-colors z-10"
            >
              <X className="w-6 h-6 text-gray-600" />
            </button>
            <img
              src={fixImageUrl(selectedImage)}
              alt="Product full size"
              className="max-w-full max-h-full object-contain rounded-lg"
              onClick={closeImageModal}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetailPage;
